import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../components/ui/chart';
import { Bar, BarChart, XAxis } from '../components/ui/chart-primitives';
import type { TrendMonth } from '@eriko/shared-types';

const chartConfig = {
  revenue: {
    label: 'Revenue',
    color: 'var(--color-neg)',
  },
  expenses: {
    label: 'Expenses',
    color: 'var(--color-olive)',
  },
} satisfies ChartConfig;

export function TrendChartSkeleton() {
  const heights = [55, 78, 42, 88, 60, 95, 50, 72, 38, 82, 66, 58];
  return (
    <div className="h-40 w-full flex items-end gap-3 px-1 animate-pulse" aria-hidden>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex-1 flex items-end gap-1">
          <div
            className="flex-1 bg-surface-200/80 rounded-sm"
            style={{ height: `${heights[i * 2]}%` }}
          />
          <div
            className="flex-1 bg-surface-200/60 rounded-sm"
            style={{ height: `${heights[i * 2 + 1]}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export function TrendChart({ data }: { data: TrendMonth[] }) {
  const chartData = data.map((m) => ({
    month: m.label,
    revenue: m.revenueHeight,
    expenses: m.expenseHeight,
  }));

  const accessibleSummary =
    data.length > 0
      ? `6-month revenue vs expenses trend from ${data[0]?.label} to ${data[data.length - 1]?.label}`
      : '6-month revenue vs expenses trend';

  return (
    <ChartContainer
      config={chartConfig}
      className="aspect-auto h-40 w-full"
      aria-label={accessibleSummary}
      role="img"
    >
      <BarChart data={chartData} barGap={3}>
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          className="font-mono text-[10px] uppercase"
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar
          dataKey="revenue"
          fill="var(--color-revenue)"
          radius={[1, 1, 0, 0]}
        />
        <Bar
          dataKey="expenses"
          fill="var(--color-expenses)"
          radius={[1, 1, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}
